import React from "react";
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

// ============================================
// TIME HELPERS
// ============================================

/**
 * Epoch millis of a measurement row: the backend's epoch `date` when present, otherwise its formatted date.
 */
export const getMeasurementTime = (item) => {
  const raw = item?.date ?? item?.formattedDate ?? item?.timestamp;
  if (raw === null || raw === undefined || raw === '') return NaN;
  if (typeof raw === 'number') return raw;
  if (/^\d+$/.test(raw)) return Number(raw);
  // "YYYY-MM-DD HH:mm:ss" is not valid ISO for every browser; the T makes it so
  return new Date(String(raw).replace(' ', 'T')).getTime();
};

/**
 * Rows sorted from oldest to newest. The API pages measurements newest first, so rows whose time
 * cannot be read are put in order by reversing them.
 */
export const chronological = (rows) => {
  if (!Array.isArray(rows)) return [];
  const times = rows.map(getMeasurementTime);
  if (times.some(Number.isNaN)) return [...rows].reverse();
  return rows
    .map((row, index) => ({ row, time: times[index] }))
    .sort((a, b) => a.time - b.time)
    .map(({ row }) => row);
};

/** Most points a single chart series draws; more only slows the browser down without adding visible detail */
export const MAX_CHART_POINTS = 120;

/**
 * Rows oldest to newest for a chart, thinned to at most MAX_CHART_POINTS evenly spaced rows
 * (the newest row is always kept). Statistics and tables use the full rows, not this.
 */
export const chartRows = (rows, limit = MAX_CHART_POINTS) => {
  const ordered = chronological(rows);
  const recent = ordered.length > limit * 20 ? ordered.slice(-limit * 20) : ordered;
  if (recent.length <= limit) return recent;
  const step = (recent.length - 1) / (limit - 1);
  return Array.from({ length: limit }, (_, i) => recent[Math.round(i * step)]);
};

/** 'YYYY-MM-DD' of a Date in the browser's time zone */
export const toDayKey = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Start and end (exclusive) of a 'YYYY-MM-DD' day in the browser's time zone, in epoch millis */
export const dayBounds = (dayKey) => {
  const [year, month, day] = dayKey.split('-').map(Number);
  return {
    from: new Date(year, month - 1, day).getTime(),
    to: new Date(year, month - 1, day + 1).getTime(),
  };
};

const formatDay = (dayKey) => {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString([], {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatTime = (millis) => new Date(millis).toLocaleString([], {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formatNumber = (value, digits = 1) =>
  value === null || value === undefined || Number.isNaN(value) ? '–' : Number(value).toFixed(digits).replace(/\.0+$/, '');

// ============================================
// ACTIVITY STATISTICS
// ============================================

export const ACTIVITY_METRICS = [
  { key: 'heartRate', label: 'Heart Rate', unit: 'bpm', color: '#3182CE', value: (a) => positive(a.bpm ?? a.heartRate) },
  { key: 'steps', label: 'Steps', unit: 'steps', color: '#38A169', value: (a) => finite(a.lastSteps ?? a.steps), showTotal: true, digits: 0 },
  { key: 'respiration', label: 'Respiration Rate', unit: 'rpm', color: '#E53E3E', value: (a) => positive(a.respirationRate) },
  { key: 'spo2', label: 'SpO2', unit: '%', color: '#DD6B20', value: (a) => positive(a.spo2) },
];

// Zero means "no reading" for these sensors, matching the backend aggregates
function positive(value) {
  return typeof value === 'number' && value > 0 ? value : null;
}

function finite(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Min/max/avg/total of each activity metric over the rows whose time lies in [range.min, range.max].
 */
export const summarizeActivities = (activities, range = null) => {
  const rows = (activities || []).filter((row) => {
    if (!range) return true;
    const time = getMeasurementTime(row);
    return time >= range.min && time <= range.max;
  });

  const summary = {};
  ACTIVITY_METRICS.forEach((metric) => {
    const values = rows.map(metric.value).filter((v) => v !== null);
    if (values.length === 0) {
      summary[metric.key] = null;
      return;
    }
    const total = values.reduce((sum, v) => sum + v, 0);
    summary[metric.key] = {
      min: Math.min(...values),
      max: Math.max(...values),
      avg: total / values.length,
      total,
      count: values.length,
    };
  });
  return { rowCount: rows.length, metrics: summary };
};

/**
 * Min/max/avg of heart rate, steps, respiration and SpO2 for the interval shown in the activity graph.
 */
export const ActivityStatsPanel = ({ activities, zoomRange, selectedDay, onResetZoom }) => {
  const panelBg = useColorModeValue("gray.50", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const { rowCount, metrics } = summarizeActivities(activities, zoomRange);

  const times = (activities || []).map(getMeasurementTime).filter((t) => !Number.isNaN(t));
  let rangeLabel;
  if (zoomRange) {
    rangeLabel = `Zoomed: ${formatTime(zoomRange.min)} – ${formatTime(zoomRange.max)}`;
  } else if (selectedDay) {
    rangeLabel = formatDay(selectedDay);
  } else if (times.length) {
    rangeLabel = `${formatTime(Math.min(...times))} – ${formatTime(Math.max(...times))} (loaded records)`;
  } else {
    rangeLabel = 'No data';
  }

  return (
    <Box mt={4} p={4} bg={panelBg} borderRadius="md">
      <HStack justify="space-between" mb={3} wrap="wrap" spacing={2}>
        <VStack align="start" spacing={0}>
          <Text fontSize="sm" fontWeight="bold">Statistics for the interval shown</Text>
          <Text fontSize="xs" color="gray.500">
            {rangeLabel} · {rowCount} readings · zoom the graph to narrow the interval
          </Text>
        </VStack>
        {zoomRange && (
          <Button size="xs" variant="outline" onClick={onResetZoom}>
            Reset zoom
          </Button>
        )}
      </HStack>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
        {ACTIVITY_METRICS.map((metric) => {
          const stats = metrics[metric.key];
          const digits = metric.digits ?? 1;
          return (
            <Box key={metric.key} p={3} bg={cardBg} borderRadius="md" borderTop="3px solid" borderColor={metric.color}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                {metric.label}
              </Text>
              {stats ? (
                <>
                  <Text fontSize="xl" fontWeight="bold">
                    {formatNumber(stats.avg, digits === 0 ? 1 : digits)}{' '}
                    <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500">avg {metric.unit}</Text>
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Min {formatNumber(stats.min, digits)} · Max {formatNumber(stats.max, digits)}
                    {metric.showTotal && ` · Total ${formatNumber(stats.total, 0)}`}
                  </Text>
                </>
              ) : (
                <Text fontSize="sm" color="gray.400" mt={1}>No readings</Text>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

// ============================================
// DAY NAVIGATION
// ============================================

/**
 * Switch between the latest records and a single day, and step from one day with data to the next.
 *
 * @param {string[]} availableDays - 'YYYY-MM-DD' days with data, ascending
 */
export const DayNavigator = ({ viewMode, selectedDay, availableDays, isLoading, onSelectDay, onShowLatest }) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const latestDay = availableDays[availableDays.length - 1];
  const earlierDays = selectedDay ? availableDays.filter((day) => day < selectedDay) : [];
  const laterDays = selectedDay ? availableDays.filter((day) => day > selectedDay) : [];
  const previousDay = earlierDays[earlierDays.length - 1];
  const nextDay = laterDays[0];
  const hasDataOnSelectedDay = selectedDay && availableDays.includes(selectedDay);

  return (
    <Box bg={cardBg} shadow="md" borderRadius="lg" p={4}>
      <HStack justify="space-between" wrap="wrap" spacing={4} rowGap={3}>
        <VStack align="start" spacing={0}>
          <Text fontWeight="bold" color="purple.600">Time window</Text>
          <Text fontSize="xs" color="gray.500">
            {viewMode === 'day' && selectedDay
              ? `${formatDay(selectedDay)}${hasDataOnSelectedDay ? '' : ' · no data on this day'}`
              : 'Latest records, newest pages first'}
            {availableDays.length > 0 && ` · ${availableDays.length} days with data`}
          </Text>
        </VStack>

        <HStack spacing={2} wrap="wrap" rowGap={2}>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              colorScheme="purple"
              variant={viewMode === 'latest' ? 'solid' : 'outline'}
              onClick={onShowLatest}
              isDisabled={isLoading}
            >
              Latest records
            </Button>
            <Button
              colorScheme="purple"
              variant={viewMode === 'day' ? 'solid' : 'outline'}
              onClick={() => onSelectDay(selectedDay || latestDay)}
              isDisabled={isLoading || availableDays.length === 0}
            >
              Day view
            </Button>
          </ButtonGroup>

          <Tooltip label={previousDay ? `Previous day with data (${previousDay})` : 'No earlier day with data'} hasArrow>
            <IconButton
              size="sm"
              aria-label="Previous day with data"
              icon={<ChevronLeftIcon boxSize={5} />}
              onClick={() => onSelectDay(viewMode === 'day' ? previousDay : latestDay)}
              isDisabled={isLoading || (viewMode === 'day' ? !previousDay : !latestDay)}
            />
          </Tooltip>
          <Input
            type="date"
            size="sm"
            width="160px"
            value={viewMode === 'day' && selectedDay ? selectedDay : ''}
            min={availableDays[0]}
            max={latestDay}
            onChange={(e) => e.target.value && onSelectDay(e.target.value)}
            isDisabled={isLoading}
          />
          <Tooltip label={nextDay ? `Next day with data (${nextDay})` : 'No later day with data'} hasArrow>
            <IconButton
              size="sm"
              aria-label="Next day with data"
              icon={<ChevronRightIcon boxSize={5} />}
              onClick={() => onSelectDay(nextDay)}
              isDisabled={isLoading || viewMode !== 'day' || !nextDay}
            />
          </Tooltip>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onSelectDay(latestDay)}
            isDisabled={isLoading || !latestDay || (viewMode === 'day' && selectedDay === latestDay)}
          >
            Latest day
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
};

// ============================================
// DAILY AGGREGATES TABLE
// ============================================

const statCell = (stats, digits = 1) =>
  stats && stats.count > 0
    ? `${formatNumber(stats.min, digits)} / ${formatNumber(stats.avg, 1)} / ${formatNumber(stats.max, digits)}`
    : '–';

/**
 * Min / avg / max of heart rate, steps, respiration and SpO2 for every day with activity data.
 * Clicking a day opens it in the graphs.
 */
export const DailyAggregatesTable = ({ days, selectedDay, onSelectDay }) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.600");
  const hoverBg = useColorModeValue("purple.50", "gray.600");
  const selectedBg = useColorModeValue("purple.100", "purple.800");
  const activityDays = (days || []).filter((day) => day.activity).slice().reverse();

  if (activityDays.length === 0) return null;

  return (
    <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
      <VStack align="start" spacing={1} mb={4}>
        <Text fontSize="lg" fontWeight="bold" color="blue.500">Daily aggregates</Text>
        <Text fontSize="sm" color="gray.600">
          Min / avg / max per day for all activity data. Steps also show the day's total. Click a day to open it in the graphs.
        </Text>
      </VStack>
      <TableContainer maxH="420px" overflowY="auto">
        <Table size="sm" variant="simple">
          <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th>Day</Th>
              <Th isNumeric>Readings</Th>
              <Th isNumeric>Heart rate (bpm)</Th>
              <Th isNumeric>Steps</Th>
              <Th isNumeric>Steps total</Th>
              <Th isNumeric>Respiration (rpm)</Th>
              <Th isNumeric>SpO2 (%)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {activityDays.map((day) => (
              <Tr
                key={day.date}
                cursor="pointer"
                bg={day.date === selectedDay ? selectedBg : undefined}
                _hover={{ bg: hoverBg }}
                onClick={() => onSelectDay(day.date)}
              >
                <Td fontWeight="medium">{formatDay(day.date)}</Td>
                <Td isNumeric>{day.activityCount}</Td>
                <Td isNumeric>{statCell(day.activity.heartRate, 0)}</Td>
                <Td isNumeric>{statCell(day.activity.steps, 0)}</Td>
                <Td isNumeric>{day.activity.steps?.total ?? '–'}</Td>
                <Td isNumeric>{statCell(day.activity.respiration)}</Td>
                <Td isNumeric>{statCell(day.activity.spo2, 0)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
